/**
 * How a notification presents itself: which family it belongs to, what mark
 * and tint it carries, and which record it opens.
 *
 * All of it is derived from data already on the record — `templateId` and the
 * ids the producers already put in `metadata`. Nothing here is hardcoded per
 * notification, and adding an event type means adding it to a family range,
 * not writing another branch.
 */
import type { NotificationRecord } from '../../models/notification-center.model';
import type { EntityModule, EntityRef } from '../entity-link/entity-link';

export type NotificationFamily =
  | 'work-order' | 'inspection' | 'action' | 'booking' | 'schedule' | 'claim' | 'system' | 'unknown';

export interface FamilyPresentation {
  family: NotificationFamily;
  /**
   * Material Icons ligature. A shape is recognised pre-attentively; a letter
   * pair has to be READ and then decoded against a legend the reader was never
   * given. macOS — the reference for this panel — puts an app icon here for the
   * same reason. Material Icons is already loaded app-wide, so this costs
   * nothing to adopt.
   */
  icon: string;
  /** Short code kept for the accessible name and as a no-font fallback. */
  mark: string;
  label: string;
  /** Maps to the --clr-* tints already defined in styles.scss. */
  tint: string;
  module: EntityModule | null;
}

/**
 * Icons are chosen to be distinguishable from each other at 20px, which matters
 * more than each being individually perfect. Booking and schedule are the pair
 * most at risk of collision, so one is a calendar (an appointment on a date)
 * and the other a clock (a plan with timing) rather than two calendars.
 */
const PRESENTATION: Record<NotificationFamily, FamilyPresentation> = {
  'work-order': { family: 'work-order', icon: 'assignment',      mark: 'WO', label: 'Work order', tint: 'indigo',  module: 'WO' },
  inspection:   { family: 'inspection', icon: 'checklist',       mark: 'IN', label: 'Inspection', tint: 'teal',    module: 'INSPECT' },
  action:       { family: 'action',     icon: 'flag',            mark: 'AC', label: 'Action',     tint: 'orange',  module: 'ACTION' },
  booking:      { family: 'booking',    icon: 'event_available', mark: 'BK', label: 'Booking',    tint: 'pink',    module: 'BOOKING' },
  schedule:     { family: 'schedule',   icon: 'schedule',        mark: 'SC', label: 'Schedule',   tint: 'sky',     module: 'SCHEDULE' },
  claim:        { family: 'claim',      icon: 'payments',        mark: '$',  label: 'Payment',    tint: 'amber',   module: 'WO' },
  // Every system notification observed in 60 days of live data is an export,
  // and an export is the ONLY thing that arrives with no templateId at all —
  // see resolveFamily for why that distinction is load-bearing.
  system:       { family: 'system',     icon: 'download',        mark: 'SY', label: 'Export',     tint: 'neutral', module: 'EXPORT' },
  // A template this build has never heard of. Deliberately dull: a correct
  // title and body with a neutral icon, rather than a confident wrong label.
  unknown:      { family: 'unknown',    icon: 'notifications',   mark: '•',  label: 'Notification', tint: 'neutral', module: null },
};

/**
 * Template id → family. Ranges mirror the seeded templates in
 * `notification-service/prisma/seed.js`; a new template joins a family by
 * falling in its list.
 */
const FAMILY_BY_TEMPLATE = new Map<number, NotificationFamily>([
  // 35: the dispatch board's visit messages (booked, moved, cancelled; COL-807). 33 and 34, the
  // "removed" messages, stay out: they link a job the person can no longer open.
  ...([2, 3, 4, 5, 6, 14, 17, 18, 19, 25, 26, 28, 35] as const).map(id => [id, 'work-order'] as const),
  ...([27] as const).map(id => [id, 'claim'] as const),            // paid → money
  ...([7, 8, 9, 15] as const).map(id => [id, 'schedule'] as const),
  ...([10, 11, 12, 13, 16] as const).map(id => [id, 'booking'] as const),
  ...([20, 21] as const).map(id => [id, 'inspection'] as const),
  ...([22, 23, 24, 29, 30] as const).map(id => [id, 'action'] as const),
]);

/**
 * The family a notification belongs to.
 *
 * TWO DIFFERENT MISSES, and they must not share an answer:
 *
 *   no templateId at all  → 'system'. Exports are the only producer that sends
 *                           none, so this genuinely is an export.
 *   a templateId not in
 *   the map above         → 'unknown'. This build has simply never heard of it.
 *
 * Both used to fall through to 'system', which meant anything unrecognised was
 * labelled "Export", given a download icon, and stacked in with the exports.
 * Template 1 (`welcome`) does that today. The bigger problem is that templates
 * live in the notification database and can be added with no deploy and no
 * commit — so template 31, whenever someone creates it, would be announced to
 * every user as an export until the frontend shipped again.
 *
 * 'unknown' is deliberately dull rather than clever: the title and body come
 * from the template and are already correct, so a neutral icon and a plain
 * "Notification" label lose nothing. Guessing loses trust.
 */
export function resolveFamily(notification: Pick<NotificationRecord, 'templateId'>): FamilyPresentation {
  if (notification.templateId == null) return PRESENTATION.system;
  return PRESENTATION[FAMILY_BY_TEMPLATE.get(Number(notification.templateId)) ?? 'unknown'];
}

function firstValue(meta: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = meta[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') return String(value).trim();
  }
  return undefined;
}

/**
 * Id keys each family's producers actually write, in priority order.
 * Taken from the live data rather than assumed — every family already carries
 * one, which is why this needs no backend change to start working.
 */
const ID_KEYS: Record<NotificationFamily, string[]> = {
  'work-order': ['woId', 'workOrderId'],
  claim:        ['woId', 'workOrderId'],
  inspection:   ['inspectionId'],
  action:       ['actionId'],
  booking:      ['bookingId'],
  schedule:     ['scheduleId', 'assignToUserId'],
  system:       ['jobId'],
  // Nothing: we do not know what this template's metadata means, so any key
  // we picked would be a guess about someone else's payload.
  unknown:      [],
};

/**
 * Absolute URLs a producer may supply. These win over any route we could
 * build: they are signed, tokenised, or point outside the app.
 */
const URL_KEYS: Record<NotificationFamily, string[]> = {
  // `docUrl` is the universal record link every producer sends (COL-679); the
  // family-specific keys remain for rows written before it existed.
  'work-order': ['docUrl', 'workOrderUrl'],
  claim:        ['docUrl', 'workOrderUrl'],
  inspection:   ['docUrl', 'inspectionUrl'],
  action:       ['docUrl', 'actionUrl'],
  booking:      ['editUrl', 'rescheduleUrl', 'cancellationUrl', 'docUrl'],
  schedule:     ['docUrl', 'scheduleUrl'],
  system:       ['downloadUrl', 'docUrl'],
  // Deliberately empty — see ID_KEYS. An unrecognised template leads nowhere
  // rather than somewhere wrong.
  unknown:      [],
};

/**
 * The record a notification points at, or null when it points at nothing.
 *
 * Reads a generic `entity` object first — the shape producers should adopt —
 * and falls back to the per-family keys they write today, so this works
 * against existing data immediately and gets simpler as producers converge.
 */
export function resolveEntity(notification: NotificationRecord): EntityRef | null {
  const meta = (notification.metadata ?? {}) as Record<string, unknown>;

  // Preferred shape: { entity: { module, id, url } } — one convention for every producer.
  const entity = meta['entity'];
  if (entity && typeof entity === 'object') {
    const e = entity as Record<string, unknown>;
    if (typeof e['module'] === 'string') {
      return {
        module: e['module'] as EntityModule,
        id: (e['id'] as string | number | undefined) ?? null,
        url: (e['url'] as string | undefined) ?? null,
        context: (e['context'] as Record<string, unknown> | undefined) ?? undefined,
      };
    }
  }

  const presentation = resolveFamily(notification);
  if (!presentation.module) return null;

  const url = firstValue(meta, URL_KEYS[presentation.family] ?? []);
  const id = firstValue(meta, ID_KEYS[presentation.family] ?? []);
  if (!url && !id) return null;

  return {
    module: presentation.module,
    id: id ?? null,
    url: url ?? null,
    context: presentation.family === 'booking'
      ? { scheduleId: meta['scheduleId'] ?? meta['assignToUserId'], eventName: meta['eventName'] }
      : undefined,
  };
}

/** A short fact shown as a pill under the body text. */
export interface NotificationFact {
  /** Rendered as `label value` when a label is present, otherwise just value. */
  label?: string;
  value: string;
}

/**
 * Metadata keys each family promotes to a pill, in priority order.
 *
 * These are keys the producers ALREADY send — `buildWorkOrderTemplateData` in
 * api-node puts woCode, priority, woStatus and preferredEndTime on every work
 * order reminder, and the sibling builders do the same for schedules and
 * bookings. The pills surface what is already on the record; nothing here needs
 * a backend change to start working.
 */
const FACT_KEYS: Record<NotificationFamily, { key: string; label?: string }[]> = {
  'work-order': [
    { key: 'preferredEndTime', label: 'Due' },
    { key: 'woType' },
    { key: 'priority' },
    { key: 'woStatus' },
  ],
  claim: [
    { key: 'woCode' },
    { key: 'preferredEndTime', label: 'Due' },
  ],
  inspection: [
    { key: 'inspectionName' },
    { key: 'completedDate', label: 'Completed' },
  ],
  action: [
    { key: 'dueDate', label: 'Due' },
    { key: 'priority' },
  ],
  booking: [
    { key: 'eventName' },
    { key: 'bookingDate' },
    { key: 'eventDuration' },
  ],
  schedule: [
    { key: 'scheduleStartDate', label: 'Starts' },
    { key: 'eventDuration' },
  ],
  system: [
    { key: 'fileName' },
    { key: 'expiresAt', label: 'Expires' },
  ],
  // No pills: promoting a key out of a payload we do not recognise is how a
  // stray internal id ends up presented to a user as if it meant something.
  unknown: [],
};

/** Values that carry no information and would just add a grey box to the row. */
const EMPTY_FACT_VALUES = new Set(['', '-', 'n/a', 'na', 'null', 'undefined', 'none', 'normal', '0']);

/** At three, pills stop being scannable and become a second paragraph. */
const MAX_FACTS = 3;

/**
 * The pills for a notification.
 *
 * Reads a generic `facts` array first — the shape producers should adopt when
 * they want to say exactly what appears — and otherwise promotes the per-family
 * keys above out of the metadata they already send.
 */
export function resolveFacts(notification: NotificationRecord): NotificationFact[] {
  const meta = (notification.metadata ?? {}) as Record<string, unknown>;

  const explicit = meta['facts'];
  if (Array.isArray(explicit)) {
    return explicit
      .map((entry) => {
        const fact = entry as Record<string, unknown>;
        const value = String(fact?.['value'] ?? '').trim();
        const label = fact?.['label'] != null ? String(fact['label']).trim() : undefined;
        return { label: label || undefined, value };
      })
      .filter((fact) => fact.value !== '' && !EMPTY_FACT_VALUES.has(fact.value.toLowerCase()))
      .slice(0, MAX_FACTS);
  }

  const family = resolveFamily(notification).family;
  const facts: NotificationFact[] = [];
  for (const { key, label } of FACT_KEYS[family] ?? []) {
    if (facts.length >= MAX_FACTS) break;
    const raw = meta[key];
    if (raw === undefined || raw === null) continue;
    const value = String(raw).trim();
    if (value === '' || EMPTY_FACT_VALUES.has(value.toLowerCase())) continue;
    facts.push(label ? { label, value } : { value });
  }
  return facts;
}

/**
 * Short relative time — "4m", "3h", "Wed", "12 Aug".
 *
 * Absolute stamps do not scan in a list; "8/20/26, 4:47PM" tells you when but
 * makes you work out how long ago. Anything older than a week gets a date,
 * because "23d" stops meaning anything.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (isNaN(then.getTime())) return '';
  const secs = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (secs < 45) return 'now';
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  if (secs < 604800) return then.toLocaleDateString(undefined, { weekday: 'short' });
  return then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Day bucket used for the list separators. */
export function dayBucket(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'Earlier';
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(then)) / 86_400_000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return 'This week';
  return 'Earlier';
}
